import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, addToast } from '@heroui/react';
import { FileText, Plus, Search } from 'lucide-react';
import DocumentsTable from './components/DocumentsTable.js';
import CredentialSection from './components/CredentialSection.js';
import DocumentDialog from './components/DocumentDialog.js';
import { addDocument, saveDocument, deleteDocument, uploadDocumentAsset } from './documentsApi.js';
import CredentialsApi, { apiErrorText } from './credentialsApi.js';
import { toApiDate, toApiTime } from './documentDates.js';
import useSectionRefresh from './useSectionRefresh.js';
import { UPLOAD_POLICIES, uploadEach, uploadSummary } from './attachments.js';

/**
 * DocumentsPage — `/users/documents/`.
 *
 * Renders from the Symfony payload and writes through `documentsApi` (Symfony
 * too). After a successful write the page reloads rather than patching local
 * state: the server computes expiries and reminders (TSA, Basic Med) that the
 * form never sends, so re-reading is the honest view of what was stored.
 */
function normalizeDocs(raw) {
	if (Array.isArray(raw)) return raw.filter(Boolean);
	if (raw && typeof raw === 'object')
		return Object.keys(raw)
			.map(k => raw[k])
			.filter(Boolean);
	return [];
}

function normalizeTypes(raw) {
	if (!raw || typeof raw !== 'object') return [];
	return Object.keys(raw)
		.map(key => {
			const v = raw[key];
			if (v == null) return null;
			return { id: String(v.id != null ? v.id : key), name: v.name != null ? v.name : String(key) };
		})
		.filter(Boolean)
		.sort((a, b) => a.name.localeCompare(b.name));
}

function DocumentsPage({ payload, onRefresh }) {
	const documents = useMemo(() => normalizeDocs(payload.documents), [payload]);
	const documentTypes = useMemo(() => normalizeTypes(payload.documentTypes), [payload]);

	const [query, setQuery] = useState('');
	const [editing, setEditing] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	// The side panel opens on the record (view) and the pencil turns it into the form.
	const [mode, setMode] = useState('view');
	const [saving, setSaving] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const [uploading, setUploading] = useState(false);
	const refresh = useSectionRefresh(onRefresh);

	// After a re-read the open drawer shows the record as now stored.
	useEffect(() => {
		setEditing(prev =>
			prev ? documents.find(d => String(d.id) === String(prev.id)) || prev : prev
		);
	}, [documents]);

	const handleUpload = useCallback(
		async (documentId, files) => {
			setUploading(true);
			const result = await uploadEach(files, file => uploadDocumentAsset(documentId, file), {
				minImageSide: UPLOAD_POLICIES.documents.minImageSide
			});
			if (result.done) await refresh();
			setUploading(false);
			addToast(uploadSummary(result, apiErrorText));
		},
		[refresh]
	);

	// Removing a scan touches no dates, so it goes straight to Symfony.
	const handleDeleteAsset = useCallback(async (documentId, item) => {
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Remove ${item.name}?`)) return;
		setUploading(true);
		try {
			await CredentialsApi.removeDocumentAsset(documentId, item.kind, item.id);
			await refresh();
			setUploading(false);
			addToast({ title: 'Scan removed', color: 'success' });
		} catch (e) {
			setUploading(false);
			addToast({ title: 'Could not remove the attachment', description: apiErrorText(e), color: 'danger' });
		}
	}, [refresh]);

	const typeName = useCallback(
		id => (documentTypes.find(t => t.id === String(id)) || {}).name,
		[documentTypes]
	);

	const visible = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return documents;
		return documents.filter(d =>
			[d.name, d.number, typeName(d.type)].filter(Boolean).join(' ').toLowerCase().includes(q)
		);
	}, [documents, query, typeName]);

	const openAdd = useCallback(() => {
		setEditing(null);
		setMode('edit');
		setDialogOpen(true);
	}, []);

	const openView = useCallback(doc => {
		setEditing(doc);
		setMode('view');
		setDialogOpen(true);
	}, []);

	const openEdit = useCallback(doc => {
		setEditing(doc);
		setMode('edit');
		setDialogOpen(true);
	}, []);

	const handleSave = useCallback(
		async (form, fields) => {
			setSaving(true);
			try {
				// Only fields the chosen type actually has carry a value; the rest go
				// as null, which the API reads as "leave it" on update. Dates are
				// `Y-m-d`, times `H:i:s` (renters insurance) — the DTO formats.
				const body = {
					name: form.name.trim(),
					typeId: Number(form.type),
					number: fields.documentNumber || fields.certificateNumber ? form.number.trim() : null,
					insuranceCompany: fields.insuranceCompany ? form.insuranceCompany : null,
					doctorsName: fields.doctorsName ? form.doctorsName : null,
					dateOfIssue: fields.dateOfIssue ? toApiDate(form.dateOfIssue) : null,
					dateExpires:
						fields.dateExpires || fields.dateExpiresTSA ? toApiDate(form.dateExpires) : null,
					timeOfIssue: fields.timeOfIssue ? toApiTime(form.dateOfIssue) : null,
					timeExpires: fields.timeExpires ? toApiTime(form.dateExpires) : null
				};

				if (form.id) await saveDocument(form.id, body);
				else await addDocument(body);

				await refresh();
				setSaving(false);
				addToast({ title: form.id ? 'Document saved' : 'Document added', color: 'success' });
				// An edit returns to the record's preview; a new one closes the panel.
				if (form.id) setMode('view');
				else setDialogOpen(false);
			} catch (e) {
				setSaving(false);
				addToast({
					title: form.id ? 'Could not save the document' : 'Could not add the document',
					description: apiErrorText(e),
					color: 'danger'
				});
			}
		},
		[refresh]
	);

	const handleDelete = useCallback(
		async doc => {
			// eslint-disable-next-line no-alert
			if (!window.confirm(`Delete “${doc.name || 'this document'}”? This cannot be undone.`))
				return;
			setBusyId(doc.id);
			try {
				await deleteDocument(doc.id);
				await refresh();
				setBusyId(null);
				setDialogOpen(false);
				addToast({ title: 'Document deleted', color: 'success' });
			} catch (e) {
				setBusyId(null);
				addToast({
					title: 'Could not delete the document',
					description: apiErrorText(e),
					color: 'danger'
				});
			}
		},
		[refresh]
	);

	return (
		<CredentialSection
			id="documents"
			icon={FileText}
			title="Documents"
			description="Photo ID, insurance and anything else the club keeps on file."
			count={documents.length}
			action={
				<Button
					color="primary"
					radius="sm"
					startContent={<Plus className="w-4 h-4" />}
					onPress={openAdd}
				>
					Add Document
				</Button>
			}
		>
			<Input
				aria-label="Search documents"
				placeholder="Search documents"
				variant="bordered"
				radius="sm"
				value={query}
				onValueChange={setQuery}
				startContent={<Search className="w-4 h-4 text-default-400" />}
				classNames={{ inputWrapper: 'h-10 min-h-10 bg-content1' }}
			/>

			<DocumentsTable
				documents={visible}
				typeName={typeName}
				onView={openView}
				onEdit={openEdit}
				onDelete={handleDelete}
				busyId={busyId}
			/>

			<DocumentDialog
				isOpen={dialogOpen}
				onClose={() => setDialogOpen(false)}
				mode={mode}
				onModeChange={setMode}
				onDelete={handleDelete}
				deleting={editing != null && busyId === editing.id}
				document={editing}
				documentTypes={documentTypes}
				onSave={handleSave}
				saving={saving}
				onUpload={handleUpload}
				onDeleteAsset={handleDeleteAsset}
				uploading={uploading}
			/>
		</CredentialSection>
	);
}

export default DocumentsPage;
